import { FC, SyntheticEvent, useEffect, useMemo, useState } from 'react'
import { NavLink, RouteObject, useLocation } from 'react-router'

import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import { COLLAPSE_ANIMATION } from '@/constants/navigationDrawer'
import { usePathname } from '@/hooks/usePathname'
import { useAuthStore } from '@/stores/useAuthStore'
import { useUiStore } from '@/stores/useUiStore'

import { NAV_MENU_TEST_IDS } from './constants'
import { NavMenuItemContent } from './NavMenuItemContent'
import { ListContainer } from './NavMenuListContainer'

type MenuItemProps = {
  item: RouteObject
  baseStyle?: boolean
  disableTooltip?: boolean
  iconless?: boolean
}

export const NavMenuItem: FC<MenuItemProps> = ({
  item,
  baseStyle = false,
  disableTooltip = false,
  iconless = false
}) => {
  const location = useLocation()
  const isNavMenuExpanded = useUiStore((s) => s.isDesktopNavExpanded)
  const platform = useAuthStore((s) => s.platform)
  const [expanded, setExpanded] = useState(false)
  const [hover, setHover] = useState(false)
  const { pathname } = usePathname()

  const { children, allChildrenWithoutIcons } = useMemo(() => {
    const children = item.children
      ?.filter((child) => !child.index && child.label && child.platforms?.includes(platform))
      .map((child) => ({ ...child, path: `${item.path}/${child.path}` }))
    const allChildrenWithoutIcons = children?.every((child) => !child.icon)

    return { children, allChildrenWithoutIcons }
  }, [item.children, item.path, platform])

  useEffect(() => {
    if (!isNavMenuExpanded) {
      setExpanded(false)
    }
  }, [isNavMenuExpanded])

  useEffect(() => {
    if (!isNavMenuExpanded || !item.children?.length) return

    if (location.pathname.includes(`/${item.path}`)) {
      setExpanded(true)
    }
  }, [])

  const toggleExpanded = (event: SyntheticEvent) => {
    if (!isNavMenuExpanded) {
      return
    }
    event.preventDefault()
    event.stopPropagation()

    setExpanded((e) => !e)
  }

  const isSelected = () => {
    if (item.exact === undefined || !item.exact || !isNavMenuExpanded || !expanded) {
      return pathname.startsWith(`/${item.path?.replaceAll('*', '')}`)
    }

    return pathname === `/${item.path}`
  }

  return (
    <Box data-testid={NAV_MENU_TEST_IDS.NAV_MENU_ITEM_LABEL.replace('{label}', item?.label || '')}>
      <NavLink
        to={`./${item?.path?.replaceAll('*', '')}`}
        style={{ color: 'inherit', textDecoration: 'none', pointerEvents: 'all' }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <NavMenuItemContent
          item={item}
          baseStyle={baseStyle}
          disableTooltip={disableTooltip}
          expanded={expanded}
          iconless={iconless}
          allChildrenWithoutIcons={allChildrenWithoutIcons}
          toggleExpanded={toggleExpanded}
          isSelected={isSelected}
          hover={hover}
          isNavMenuExpanded={isNavMenuExpanded}
        >
          {children?.length ? children : undefined}
        </NavMenuItemContent>
      </NavLink>
      {!!children?.length && (
        <Collapse in={expanded}>
          <ListContainer
            sx={{
              ml: 2.5,
              transition: COLLAPSE_ANIMATION,
              opacity: isNavMenuExpanded ? 1 : 0
            }}
          >
            {children.map((child) => (
              <NavMenuItem key={child.label} item={child} iconless={allChildrenWithoutIcons} />
            ))}
          </ListContainer>
        </Collapse>
      )}
    </Box>
  )
}
